<template>
  <BModal :id="id" :visible="show" max-width="500" scrollable hide-footer no-close-on-backdrop @hide="$emit('hide')">
    <template #modal-header>
      <h5 class="mb-auto mt-auto ml-auto mr-auto modal-title">Modify Quantity</h5>
      <button type="button" class="modal-close" aria-label="Close" @click="$emit('hide')">
        <i class="fa fa-times-thin" style="font-size: 1.5em" />
      </button>
    </template>
    <div v-if="batchLoading">
      <Loading />
    </div>
    <div v-else>
      <div v-if="item.sample || item.quantity === 0" class="mx-3 text-center text-primary">
        <p>This is a sample. You can remove it, but you can't add to a sample quantity.</p>
        <BushelBtn variant="danger" outline class="btn-block mt-3" :disabled="submitDisabled" @click="modifyQuantity"> Remove </BushelBtn>
      </div>
      <div v-else class="mx-3 text-center">
        <label>{{ getQuantityLabel() }}</label
        ><br />
        <div>
          <label v-if="quantityOnOtherLineItem > 0 && item.category_name.toLowerCase() == 'pack to order'">
            Note {{ quantityOnOtherLineItem }} Grams on other line item
          </label>
        </div>
        <div class="mb-2 d-flex mt-2">
          <BushelTextInput
            v-if="item.category_name"
            v-model="quantity"
            class="flex-grow-1"
            thin
            label-active="New Quantity"
            type="number"
            :number-type-allow-decimal="categoryTypeCanHaveDecimal(item.category_name, item, true)"
            :number-type-max-precision="6"
            name="addingProductQuantity"
            :suffix="getMeasurement()"
          />
          <BushelBtn variant="danger" style="margin-top: 24px" class="btn-block ml-2" outline inline-form-class @click="quantity = 0">
            <i class="fa fa-times" />
          </BushelBtn>
        </div>
        <div v-if="quantity !== '' && quantity !== null && originalQuantity != quantity">
          <div v-if="!submitDisabled">
            <h5 class="mb-0 text-primary">Original Quantity: {{ originalQuantity }} {{ getMeasurement() }}</h5>
            <h5 class="mb-0 text-primary">Quantity Change: {{ quantityChange > 0 ? "+" : "" }}{{ quantityChange }} {{ getMeasurement() }}</h5>
          </div>
          <div v-else class="alert alert-warning">Exceeds Available Inventory Amount</div>
          <div class="row mt-3">
            <div class="col">
              <BushelBtn :disabled="submitDisabled" outline variant="success" class="btn-block mt-3" @click="modifyQuantity"> Update </BushelBtn>
            </div>
          </div>
        </div>
      </div>
    </div>
  </BModal>
</template>

<script>
import { categoryTypeCanHaveDecimal } from "@/services/inventory/inventory";
import { getUnit } from "@/services/storefront/storefront";
import * as api from "@/services/api";

export default {
  components: {},
  props: {
    show: { type: Boolean, required: true },
    id: { type: String, default: "modifyQuantiyLineItemCartModal" },
    item: { type: Object, required: true },
    quantityOnOtherLineItem: { type: Number, required: true },
  },
  emits: ["modificationConfirmed", "hide"],
  data() {
    return {
      batch: null,
      batchLoading: true,
      originalQuantity: this.item.pack_to_order_quantity ? this.item.pack_to_order_quantity : this.item.quantity,
      quantity: this.item.sample
        ? 0
        : this.item.pack_to_order_quantity
        ? this.item.pack_to_order_quantity
        : this.item.quantity
        ? this.item.quantity
        : 0,
    };
  },
  computed: {
    quantityChange() {
      let maxDecimals = this.countDecimals(this.originalQuantity);
      const newDecimalCount = this.countDecimals(this.quantity);
      if (newDecimalCount > maxDecimals) {
        maxDecimals = newDecimalCount;
      }
      if (maxDecimals > 6) {
        maxDecimals = 6;
      }

      const change = this.quantity - this.originalQuantity;
      return change.toFixed(maxDecimals);
    },
    submitDisabled() {
      if (!this.batch || !this.batch.id) {
        return true;
      }
      if (this.quantity === "null" || this.quantity === "" || this.quantity === undefined) {
        return true;
      }
      if (this.batch.disable_inventory_tracking) {
        return false;
      }
      let computedTotal = 0;
      if (this.item.pack_to_order_type && !this.item.sample) {
        switch (this.item.pack_to_order_type) {
          case "1 Gram":
            computedTotal = this.quantity;
            break;
          case "2 Gram":
            computedTotal = this.quantity * 2;
            break;
          case "1/8th Oz":
            computedTotal = this.quantity * 3.5;
            break;
          case "1/4 Oz":
            computedTotal = this.quantity * 7;
            break;
          case "1/2 Oz":
            computedTotal = this.quantity * 14;
            break;
          case "1 Oz":
            computedTotal = this.quantity * 28;
            break;
          case "1/4 Lb":
            computedTotal = this.quantity * 113.5;
            break;
          case "1/2 Lb":
            computedTotal = this.quantity * 227;
            break;
          case "1 Lb":
            computedTotal = this.quantity * 454;
            break;
          default:
            computedTotal = 0;
        }
      } else {
        computedTotal = this.quantity;
      }
      if (computedTotal <= this.batch.quantity) {
        return false;
      }

      return true;
    },
  },
  mounted() {
    this.fetchBatch();
  },
  methods: {
    categoryTypeCanHaveDecimal,
    getUnit,
    countDecimals(value) {
      if (!value) return 0;
      if (Math.floor(value) === value) return 0;
      if (value.toString().split(".")[1]) {
        return value.toString().split(".")[1].length || 0;
      }
      return 0;
    },
    fetchBatch() {
      this.batchLoading = true;
      // look it up again. helps prevent chance of quantity being off.
      api.get(`storefront/get-base-batch/${this.item.batch_id}`).then((response) => {
        this.batchLoading = false;
        if (response.data && response.data.id) {
          this.batch = response.data;
        }
      });
    },
    getQuantityLabel() {
      let returnString = `(${"Total Available"}`;

      if (this.batch && this.batch.id) {
        if (this.batch.disable_inventory_tracking) {
          returnString = `${returnString}: ${"Unlimited"})`;
        } else if (this.item.pack_to_order_type) {
          switch (this.item.pack_to_order_type) {
            case "1 Gram":
              returnString = `${returnString}: ${this.batch.quantity} Grams | ${this.batch.quantity}`;
              break;
            case "2 Gram":
              returnString = `${returnString}: ${this.batch.quantity} Grams | ${Math.floor(this.batch.quantity / 2)}`;
              break;
            case "1/8th Oz":
              returnString = `${returnString}: ${this.batch.quantity} Grams | ${Math.floor(this.batch.quantity / 3.5)}`;
              break;
            case "1/4 Oz":
              returnString = `${returnString}: ${this.batch.quantity} Grams | ${Math.floor(this.batch.quantity / 7)}`;
              break;
            case "1/2 Oz":
              returnString = `${returnString}: ${this.batch.quantity} Grams | ${Math.floor(this.batch.quantity / 14)}`;
              break;
            case "1 Oz":
              returnString = `${returnString}: ${this.batch.quantity} Grams | ${Math.floor(this.batch.quantity / 28)}`;
              break;
            case "1/4 Lb":
              returnString = `${returnString}: ${this.batch.quantity} Grams | ${Math.floor(this.batch.quantity / 113.5)}`;
              break;
            case "1/2 Lb":
              returnString = `${returnString}: ${this.batch.quantity} Grams | ${Math.floor(this.batch.quantity / 227)}`;
              break;
            case "1 Lb":
              returnString = `${returnString}: ${this.batch.quantity} Grams | ${Math.floor(this.batch.quantity / 454)}`;
              break;
            default:
              returnString = `${returnString}: error`;
          }
          returnString = `${returnString} ${"packs"})`;
        } else {
          returnString = `${returnString}: ${this.batch.quantity} ${this.getUnit(this.item).name})`;
        }

        if (this.item.sample) {
          returnString = `${returnString} (${"Sample"})`;
        }
      }

      return returnString;
    },
    getMeasurement() {
      if (this.item.pack_to_order_quantity) {
        return `${this.item.pack_to_order_type} ${"packs"}`;
      }
      const returnString = `${this.getUnit(this.item).name}`;
      return returnString;
    },

    modifyQuantity() {
      let { quantity, quantityChange } = this;
      if (!this.categoryTypeCanHaveDecimal(this.item.category_name, this.item, true) && quantity % 1 != 0) {
        quantity = parseInt(quantity, 10);
      }

      if (this.item && this.item.sample) {
        quantity = 0;
      }
      const payload = {
        quantity,
        itemId: this.item.id,
        cartId: this.item.cart_id,
      };

      if (this.item.pack_to_order_type) {
        payload.pto_product = true;
        switch (this.item.pack_to_order_type) {
          case "1 Gram":
            payload.pto_oneg_quantity = quantity;
            payload.quantity = quantity;
            break;
          case "2 Gram":
            payload.pto_twog_quantity = quantity;
            payload.quantity = quantity * 2;
            break;
          case "1/8th Oz":
            payload.pto_eighthoz_quantity = quantity;
            payload.quantity = quantity * 3.5;
            break;
          case "1/4 Oz":
            payload.pto_quarteroz_quantity = quantity;
            payload.quantity = quantity * 7;
            break;
          case "1/2 Oz":
            payload.pto_halfoz_quantity = quantity;
            payload.quantity = quantity * 14;
            break;
          case "1 Oz":
            payload.pto_oneoz_quantity = quantity;
            payload.quantity = quantity * 28;
            break;
          case "1/4 Lb":
            payload.pto_quarterpound_quantity = quantity;
            payload.quantity = quantity * 113.5;
            break;
          case "1/2 Lb":
            payload.pto_halfpound_quantity = quantity;
            payload.quantity = quantity * 227;
            break;
          case "1 Lb":
            payload.pto_onepound_quantity = quantity;
            payload.quantity = quantity * 454;
            break;
          default:
        }
      }

      this.$emit("modificationConfirmed", payload);
      this.$emit("hide");
    },
  },
};
</script>
